import { Component, OnInit, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { Store } from '@ngxs/store';

export enum WelcomeTourEnum {
  NewUser,
  ExistingUser,
  Group
}

export interface WelcomeTourDialogData {
  userType: WelcomeTourEnum;
}

class SlideData {
  logo?: string;
  title?: string;
  text?: string;
  image?: string;
}

const baseWelcomeSlides: SlideData[] = [
  {
    title: 'Explore',
    text: 'Browse our digital library for 3D models to complement your curriculum.',
    image: '/assets/images/welcome-tour-explore.jpg'
  },
  {
    title: 'Customize',
    text: 'Use our authoring tools to create 3D views tailored to your educational goals.',
    image: '/assets/images/welcome-tour-customize.jpg'
  },
  {
    title: 'Collaborate',
    text: 'Share your saved 3D views with your team from a central Dashboard.',
    image: '/assets/images/welcome-tour-collaborate.jpg'
  },
  {
    title: 'Integrate',
    text: 'Publish your interactive 3D model and drop into your digital content.',
    image: '/assets/images/welcome-tour-integrate.jpg'
  }
];

const newUserSlides: SlideData[] = [
  {
    text: 'We make it easy to visualize the human body<br/> for your educational needs - all in interactive 3D.',
    logo: '/assets/images/biodigital-logo.svg'
  },
  ...baseWelcomeSlides

];
const existingUserSlides: SlideData[] = [
  {
    title: 'Introducing Human Studio',
    text: 'The first product for authoring custom interactive 3D models of anatomy, disease and treatments.',
    image: '/assets/images/studio-tour-studio.jpg'
  },
  {
    title: 'Interactive Labels',
    text: 'Quickly add anatomical labels to call out key learning objectives.',
    image: '/assets/images/studio-tour-labels.jpg'
  },
  {
    title: 'Paint',
    text: 'Customize the look and feel of your 3D model by applying different paint effects to anatomical structures.',
    image: '/assets/images/studio-tour-paint.jpg'
  },
  {
    title: 'Tour Builder',
    text: 'Tell an interactive story by stringing together different views of your 3D model.',
    image: '/assets/images/studio-tour-tour.jpg'
  },
  {
    title: 'Text Manager',
    text: 'Add or modify text from one dedicated place.',
    image: '/assets/images/studio-tour-text.jpg'
  }
];

@Component({
  selector: 'app-welcome-tour-dialog',
  templateUrl: './welcome-tour-dialog.component.html',
  styleUrls: ['./welcome-tour-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WelcomeTourDialogComponent implements OnInit, OnDestroy {

  private ngUnsubscribe = new Subject();
  settings: WelcomeTourDialogData = {
    userType: WelcomeTourEnum.NewUser
  };

  slides: SlideData[] = [];
  selectedIndex = 0;

  get isFirstSlide(): boolean {
    return this.selectedIndex === 0;
  }

  get isLastSlide(): boolean {
    return this.selectedIndex === this.slides.length - 1;
  }

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<WelcomeTourDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: WelcomeTourDialogData
  ) {
    this.settings = {
      ...this.settings,
      ...data
    };
    // set data source
    const type = this.settings.userType;
    switch (type) {
      case WelcomeTourEnum.NewUser:
        this.slides = newUserSlides;
        break;
      case WelcomeTourEnum.ExistingUser:
        this.slides = existingUserSlides;
        break;
      default:
        break;
    }
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleCloseTour() {
    this.close();
  }

  close() {
    // close dialog
    this.dialogRef.close();
  }

  handleSelectSlideClick(index) {
    this.selectedIndex = index;
  }

  handleNextClick() {
    this.incrementSelectedIndex(1);
  }

  handlePrevClick() {
    this.incrementSelectedIndex(-1);
  }

  incrementSelectedIndex(increment: number) {
    const targetIndex = this.selectedIndex + increment;
    this.selectedIndex = Math.max(0, targetIndex) % this.slides.length;
  }

}
